/**
 * @pubemhu
 * @description קומפוננטת נגישות המאפשרת התאמות שונות לפי תקן הנגישות הישראלי
 * @author Ben Ami
 * @version 1.0.0
 */

import React, { useState, useEffect } from 'react';

// סטיילים קבועים שלא יושפעו מהגדרות הניגודיות
const accessibilityButtonStyles = {
  button: {
    position: 'sticky',
    left: '1.5rem',
    bottom: '1.5rem',
    width: '52px',
    height: '52px',
    backgroundColor: '#2563EB',
    color: '#FFFFFF',
    border: '2px solid #FFFFFF',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.3s ease-in-out',
    zIndex: 10,
    padding: '0',
    margin: '0',
    outline: 'none',
    ':hover': {
      transform: 'scale(1.1)',
      backgroundColor: '#1D4ED8'
    },
    ':focus': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), 0 0 0 4px rgba(59, 130, 246, 0.5)'
    }
  },
  icon: {
    width: '28px',
    height: '28px',
    transition: 'transform 0.3s ease-in-out',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
  },
  label: {
    position: 'absolute',
    top: '50%',
    left: 'calc(100% + 12px)',
    transform: 'translateY(-50%)',
    backgroundColor: '#FFFFFF',
    color: '#2563EB',
    padding: '6px 12px',
    borderRadius: '9999px',
    fontSize: '14px',
    fontWeight: 'bold',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    opacity: '0',
    transition: 'all 0.3s ease-in-out',
    pointerEvents: 'none',
    whiteSpace: 'nowrap'
  }
};

const Accessibility = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [settings, setSettings] = useState({
    fontSize: 1,
    contrast: 'normal',
    animations: true,
    cursor: 'default'
  });

  useEffect(() => {
    setMounted(true);
    // טעינת הגדרות מ-localStorage
    const savedSettings = localStorage.getItem('accessibility-settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings(parsed);
      } catch (e) {
        console.error('Error loading accessibility settings:', e);
      }
    }
  }, []);

  useEffect(() => {
    if (!mounted) return;
    
    // עדכון הגדרות CSS גלובליות
    document.documentElement.style.setProperty('--font-size-multiplier', settings.fontSize);
    
    // ניהול classes לניגודיות
    document.body.classList.remove('contrast-normal', 'contrast-high', 'contrast-dark');
    document.body.classList.add(`contrast-${settings.contrast}`);
    
    // ניהול סמן עכבר
    if (settings.cursor === 'large') {
      document.body.style.cursor = 'url("data:image/svg+xml,%3Csvg width=\'32\' height=\'32\' viewBox=\'0 0 32 32\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M8.971 23.029L23.029 8.97\' stroke=\'black\' stroke-width=\'2\'/%3E%3C/svg%3E"), auto';
    } else {
      document.body.style.cursor = 'default';
    }
    
    // ניהול אנימציות
    if (!settings.animations) {
      document.body.classList.add('reduce-motion');
    } else {
      document.body.classList.remove('reduce-motion');
    }
  }, [settings, mounted]);

  const updateSetting = (key, value) => {
    setSettings(prev => {
      const newSettings = { ...prev, [key]: value };
      localStorage.setItem('accessibility-settings', JSON.stringify(newSettings));
      return newSettings;
    });
  };

  if (!mounted) return null;

  return (
    <>
      {/* כפתור נגישות */}
      <div style={{ 
        position: 'sticky', 
        left: '1.5rem', 
        bottom: '1.5rem',
        zIndex: 10,
        marginTop: 'auto'
      }}>
        <button
          onClick={() => setIsOpen(true)}
          aria-label="פתח תפריט נגישות"
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.querySelector('.accessibility-label').style.opacity = '1';
            e.currentTarget.querySelector('.accessibility-label').style.transform = 'translate(calc(100% + 12px), -50%) scale(1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.querySelector('.accessibility-label').style.opacity = '0';
            e.currentTarget.querySelector('.accessibility-label').style.transform = 'translate(calc(100% + 8px), -50%) scale(0.95)';
          }}
          style={accessibilityButtonStyles.button}
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor"
            style={accessibilityButtonStyles.icon}
          >
            <circle cx="12" cy="12" r="10" strokeWidth="2.5"/>
            <path d="M12 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" fill="currentColor"/>
            <path d="M10 12h4" strokeWidth="2.5" strokeLinecap="round"/>
            <path d="M12 10v7" strokeWidth="2.5" strokeLinecap="round"/>
            <path d="M8 16l3-3" strokeWidth="2.5" strokeLinecap="round"/>
            <path d="M16 16l-3-3" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
          <span className="accessibility-label" style={{
            ...accessibilityButtonStyles.label,
            transform: 'translate(calc(100% + 8px), -50%) scale(0.95)'
          }}>
            נגישות
          </span>
        </button>
      </div>

      {/* דיאלוג נגישות */}
      {isOpen && (
        <div 
          style={{
            position: 'fixed',
            inset: '0',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 99999,
            overflow: 'auto'
          }}
          onClick={(e) => e.target === e.currentTarget && setIsOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg transform transition-all duration-300 ease-in-out">
              {/* כותרת */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full p-1"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <h3 className="text-xl font-bold text-gray-900" id="modal-title">
                  הגדרות נגישות
                </h3>
              </div>

              {/* תוכן */}
              <div className="p-6 space-y-8">
                {/* גודל טקסט */}
                <div className="space-y-4">
                  <div className="flex items-center justify-end gap-2">
                    <h4 className="text-lg font-medium text-gray-900">גודל טקסט</h4>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                    </svg>
                  </div>
                  <div className="flex gap-3 justify-end">
                    {[0.8, 1, 1.2, 1.4].map(size => (
                      <button
                        key={size}
                        onClick={() => updateSetting('fontSize', size)}
                        className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                          settings.fontSize === size 
                            ? 'bg-blue-600 text-white shadow-lg transform scale-105' 
                            : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                      >
                        {size === 1 ? 'רגיל' : `${size * 100}%`}
                      </button>
                    ))}
                  </div>
                </div>

                {/* ניגודיות */}
                <div className="space-y-4">
                  <div className="flex items-center justify-end gap-2">
                    <h4 className="text-lg font-medium text-gray-900">ניגודיות</h4>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                  </div>
                  <div className="flex gap-3 justify-end">
                    {['normal', 'high', 'dark'].map(contrast => (
                      <button
                        key={contrast}
                        onClick={() => updateSetting('contrast', contrast)}
                        className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                          settings.contrast === contrast 
                            ? 'bg-blue-600 text-white shadow-lg transform scale-105' 
                            : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                      >
                        {contrast === 'normal' ? 'רגיל' : contrast === 'high' ? 'ניגודיות גבוהה' : 'מצב כהה'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* אנימציות */}
                <div className="space-y-4">
                  <div className="flex items-center justify-end gap-2">
                    <h4 className="text-lg font-medium text-gray-900">אנימציות</h4>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                    </svg>
                  </div>
                  <label className="flex items-center justify-end gap-4 cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={settings.animations}
                        onChange={(e) => updateSetting('animations', e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`w-14 h-7 rounded-full transition-all duration-200 ${settings.animations ? 'bg-blue-600' : 'bg-gray-200'}`}>
                        <div className={`absolute w-5 h-5 rounded-full bg-white shadow transform transition-transform duration-200 ${settings.animations ? 'translate-x-1 translate-y-1' : 'translate-x-8 translate-y-1'}`} />
                      </div>
                    </div>
                    <span className="text-gray-700">הפעל אנימציות</span>
                  </label>
                </div>

                {/* סמן עכבר */}
                <div className="space-y-4">
                  <div className="flex items-center justify-end gap-2">
                    <h4 className="text-lg font-medium text-gray-900">סמן עכבר</h4>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                    </svg>
                  </div>
                  <div className="flex gap-3 justify-end">
                    {['default', 'large'].map(cursor => (
                      <button
                        key={cursor}
                        onClick={() => updateSetting('cursor', cursor)}
                        className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                          settings.cursor === cursor 
                            ? 'bg-blue-600 text-white shadow-lg transform scale-105' 
                            : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                      >
                        {cursor === 'default' ? 'רגיל' : 'מוגדל'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* כפתור סגירה */}
              <div className="border-t border-gray-200 p-6">
                <button
                  type="button"
                  className="w-full inline-flex justify-center items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                  onClick={() => setIsOpen(false)}
                >
                  <span>שמור וסגור</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Accessibility; 